import {
  type RovoContentBridgeAPI,
  type client,
} from '@atlassian/rovo-content-bridge-api';
import { type ActivityLogger } from '@databases/logs/activity-logger';
import {
  type PanelKeyToPropsMapping,
  type PanelKeys,
  PanelManager,
} from '@databases/panel-types';

import {
  ShowPanelCommand,
  HidePanelCommand,
  PanelType,
} from '@canvas/databases-bridge-api/commands';

/**
 * Panel manager used in native embed mode.
 * Like the other manager, it updates its exposed state and also forwards calls over the bridge client.
 */
export class NativeEmbedPanelManager extends PanelManager {
  #panelKeyToBridgeMap: Record<Exclude<PanelKeys, 'DevtoolsPanel'>, PanelType> = {
    DatabaseSetupPanel: PanelType.DatabaseSetup,
    DuplicateStructurePanel: PanelType.DuplicateStructure,
    EntryViewPanel: PanelType.EntryView,
    FieldCreatePanel: PanelType.FieldCreate,
    FieldSettingsPanel: PanelType.FieldSettings,
    ViewSettingsPanel: PanelType.ViewSettings,
  };

  constructor(
    private readonly bridge: RovoContentBridgeAPI,
    private readonly clients: {
      localClientId: ReturnType<typeof client>;
      externalClientId: ReturnType<typeof client>;
    },
    private readonly activityLogger: ActivityLogger
  ) {
    super();
  }

  public override showPanel<key extends PanelKeys>(
    panelKey: key,
    data?: PanelKeyToPropsMapping[key]
  ) {
    this.state.panel = panelKey;
    this.state.data = data;

    this.bridge.send(ShowPanelCommand, {
      senderId: this.clients.localClientId,
      receiverId: this.clients.externalClientId,
      type: this.#panelKeyToBridgeMap[panelKey as Exclude<PanelKeys, 'DevtoolsPanel'>],
    });

    this.activityLogger.log('showPanel', { panelKey: this.state.panel, ...data });
  }

  public override hidePanel() {
    this.activityLogger.log('hidePanel', { panelKey: this.state.panel });

    this.state.panel = null;
    this.state.data = undefined;

    this.bridge.send(HidePanelCommand, {
      senderId: this.clients.localClientId,
      receiverId: this.clients.externalClientId,
    });
  }
}
